// Compile-time binding of prev_obj backreferences ("it", "the card you drew", "both of them").
// Plan: docs/prev-obj-static-binding-plan.md
//
// Phase 1: this only *stamps* bindings onto the parsed tree. Nothing reads them yet –
// resolvePrevObj() still scans the runtime history. Slots are plain numbers on action
// nodes, so they survive clone() and the {...a} spreads in singleAction/parsedAction.
//
//   action node   -> a.slot = 0, 1, 2, ...  (per pegParse result)
//   prev_obj node -> d.bind  = {kind: 'slot', slot, part} | {kind: 'slots', slots}
//                            | {kind: 'trigger'} | {kind: 'loop'} | {kind: 'self'}
//                            | {kind: 'count', slot} | {kind: 'same'} | {kind: 'unbound'}
//
// The walk is deliberately structural: it knows about prev_obj, action nodes, and the few
// node types that introduce a referent (choice, condition, action_per, reaction, goal
// elements). It keeps no inventory of grammar keys, so new grammar rules need no edit here.
import { res } from './stores.js';
import { get } from 'svelte/store';

// Shared with parse.js, which imports both: one definition, one behaviour.
export const synonyms = {
    'lose': 'spend',
    'free play': 'freeplay',
    'perform': 'resolve'
};

export function verbMatches(v1, v2){
    if(!v1 || !v2) return false;
    v1 = synonyms[v1] || v1;
    v2 = synonyms[v2] || v2;
    if(v1 == v2) return true;
    if(v1 == 'log_assimilate' && ['log', 'assimilate'].includes(v2)) return true;
    if(v2 == 'log_assimilate' && ['log', 'assimilate'].includes(v1)) return true;
    if(v1 == 'gain' && v2.startsWith('gain_')) return true;
    if(v2 == 'gain' && v1.startsWith('gain_')) return true;
    if([v1, v2].includes('freeplay') && [v1, v2].includes('play')) return true;
    return false;
}

function possArray(v){
    if(v === null || v === undefined) return [];
    return Array.isArray(v) ? v : [v];
}

// Types that are not cards. Tokens and tracks come from the same store the runtime uses;
// the rest are the ones typeOfGain() knows about that res does not name.
const OTHER_NON_CARD = ['action', 'away', 'away team', 'opp_away', 'murf', 'drone'];

function nonCardType(t){
    let r = get(res) || {};
    return [...possArray(r.resourceTypes), ...possArray(r.tokenTypes), ...possArray(r.trackTypes),
        ...Object.values(r.extraTokenTypes || {}).flat(), ...OTHER_NON_CARD].includes(t);
}

// Binding only needs card vs not-card: a reference to "the card you gained" must not land on
// "gain 1 [[glory]]". Finer kinds are the type name itself, so tracks and tokens stay apart.
function typeKind(t){
    if(!t) return 'card';
    if(Array.isArray(t)){
        let kinds = Array.from(new Set(t.flat(2).map(typeKind)));
        return kinds.length == 1 ? kinds[0] : 'card';
    }
    if(typeof t == 'object'){
        if(t.specific) return typeKind(t.specific);
        if(t.types) return typeKind(t.types);
        if(t.either_or) return typeKind(t.either_or);
        return 'card';
    }
    if(typeof t != 'string') return 'card';
    return nonCardType(t.toLowerCase()) ? 'not_card' : 'card';
}

// What an action leaves behind for a later reference to point at.
function kindOfObj(obj){
    if(obj === null || obj === undefined || obj === false) return 'none';
    if(typeof obj == 'string') return 'card'; // 'this card', 'target', 'reserve'...
    if(Array.isArray(obj)){
        let kinds = obj.map(kindOfObj).filter(k => k != 'none');
        if(kinds.length == 0) return 'none';
        return Array.from(new Set(kinds)).length == 1 ? kinds[0] : 'card';
    }
    if(obj.card || obj.place || obj.card_itself) return 'card';
    if(obj.types) return typeKind(obj.types);
    if(obj.type && typeof obj.type == 'string' && obj.type != 'prev_obj') return typeKind(obj.type);
    return 'card'; // permissive default: most objects are cards
}

function isBackreference(d){
    return d && typeof d == 'object' && d.type == 'prev_obj';
}

function isAction(node){
    return node && node.type == 'action' && typeof node.verb == 'string';
}

// "this card" / "this ship" / "this [[ship]]" – the card being resolved, not an earlier result.
function isSelfObj(obj){
    let card = typeof obj == 'string' ? obj : obj && typeof obj.card == 'string' ? obj.card : null;
    return !!card && card.toLowerCase().startsWith('this ');
}

const SELF = {slot: 'self', verbs: [], parts: {obj: 'card'}, get suits(){ return {obj: selfSuits}; }};

// Referents the runtime names rather than numbers: 'self', 'trigger', 'loop'. Real slots are
// `<unit>:<n>` (see nextSlot), so the colon is what tells them apart.
function isPseudoSlot(slot){
    return typeof slot == 'string' && !slot.includes(':');
}

// A condition, loop or trigger selects an object its body can then call "it"/"them". Such a
// referent has only the one part, unlike an action with its to/from.
function selectEntry(obj, slot, verbs = [], via = null){
    let e = {slot, verbs, parts: {obj: kindOfObj(obj), to: 'none', from: 'none'}, suits: {obj: suitsOfObj(obj)}};
    if(via) e.via = via; // the runtime does not record this kind of referent yet
    return e;
}

// True unless the action explicitly produces several objects ("all", or an explicit count > 1).
// A singular "it" must not bind to a many-producer ("dismiss all cards ... then assimilate it").
function producesOne(node){
    let o = node.obj;
    let n = o && typeof o == 'object' && !Array.isArray(o) ? o.num : node.num;
    if(n === 'all' || n === 'any') return false;
    return !(typeof n == 'number' && n > 1);
}

// "discard a card": the object has no type of its own, so a later "that [[ship]]" cannot be it.
function isGenericCard(obj){
    return !!obj && typeof obj == 'object' && !Array.isArray(obj) && obj.card === 'card' && !obj.types;
}

function entryFor(node, slot){
    let e = {
        slot,
        verbs: [node.verb, ...possArray(node.extraVerbs).map(v => v && v.verb)].filter(v => typeof v == 'string'),
        // some verbs (gain_top, scan_for...) carry their types on the node instead of in obj
        parts: {obj: kindOfObj(node.obj || (node.types ? {types: node.types} : null)), to: kindOfObj(node.to), from: kindOfObj(node.from)},
        single: producesOne(node),
        generic: isGenericCard(node.obj),
        objTypes: node.obj && typeof node.obj == 'object' && !Array.isArray(node.obj) ? node.obj.types : null,
        suits: {obj: suitsOfObj(node.obj || (node.types ? {types: node.types} : null)), to: suitsOfObj(node.to), from: suitsOfObj(node.from)}
    };
    e.zone = zoneAfter(e.verbs);
    // "dismiss a [[person]] beamed to a [[ship]] and exhaust that [[ship]]": where the object was
    // beamed is where it came from
    let beamedTo = node.obj && typeof node.obj == 'object' && node.obj.location && node.obj.location.beamed_to;
    if(beamedTo && e.parts.from == 'none'){
        e.parts.from = 'card';
        e.suits.from = suitsOfType(beamedTo);
    }
    // "it engages" acts on an earlier referent rather than introducing one, so a later "it"
    // outside its branch means that same referent. Binding it to this action instead breaks
    // when the branch never ran ("if ... it engages, otherwise it explores").
    let o = node.obj && typeof node.obj == 'object' ? node.obj : null;
    let ref = o && (isBackreference(o) ? o : isBackreference(o.card) ? o.card : null);
    if(ref && ref.bind && !['unbound', 'same', 'count'].includes(ref.bind.kind)) e.alias = ref.bind;
    return e;
}

// "[[sync]] is 4+" counts several cards at once, so a singular "it" in the body is not one of
// them. ("2+ token there" counts tokens, and the existing binding there is left alone.)
function testsMany(node){
    let types = node.obj && node.obj.types;
    if(possArray(types).includes('token') || typeKind(types) != 'card') return false;
    return [node.num, node.obj && node.obj.num].some(n => n === 'all'
        || (typeof n == 'number' && n > 1)
        || (n && typeof n == 'object' && ((n.min || 0) > 1 || (n.num || 0) > 1)));
}

// "If there is a [[person]] in the Bot Discard pile, promote them": the test names exactly one,
// so a bare "them" in the body is just a gender-neutral singular, not a plural.
function testsOne(node){
    let types = node.obj && node.obj.types;
    if(possArray(types).includes('token') || typeKind(types) != 'card') return false;
    return [node.num, node.obj && node.obj.num].some(n => n === 1);
}

// The verbs a descriptor asks for, e.g. {prevVerb: {verb: 'gain'}} or {verb: ['draw','take']}.
function wantedVerbs(d){
    if(!d.prevVerb) return null;
    let v = d.prevVerb.verb !== undefined ? d.prevVerb.verb : d.prevVerb;
    let list = possArray(v).filter(x => typeof x == 'string');
    return list.length ? list : null;
}

function wantedKind(d){
    if(d.filter && d.filter.types) return typeKind(d.filter.types);
    if(d.prevType && d.prevType != 'card' && d.prevType != 'cards') return typeKind(d.prevType);
    return 'card'; // resolvePrevObj filters to cards by default
}

// Which part of an earlier action ('the card you discarded' / 'the location you warped to')
// this reference points at, or null if that action cannot be the referent. With `loose`, the
// verb is a preference rather than a requirement: card text names verbs the parsed action does
// not always carry ("the gained card" after a scan, "the drawn card" after draw_reveal).
// "recall a deployed [[ship]] from the same [[location]]. If that [[location]] is neutral...":
// the action's object is a typed non-location card, so "that [[location]]" is where it came
// from (or went to), not the card itself - which may well have left the table by now.
function locationPart(e){
    let types = possArray(e.objTypes).flat(2).filter(t => typeof t == 'string');
    if(!types.length || types.some(t => t.toLowerCase().includes('location'))) return null;
    for(let part of ['from', 'to']) if(e.parts[part] && e.parts[part] != 'none') return part;
    return null;
}

// `accept(part)` can veto a part the reference cannot mean, and prefers one known to fit
// (see couldBe): "beam this card to a [[ship]]. Refresh that [[ship]]" is the ship, not this card.
function matchPart(e, d, loose = false, accept = () => true){
    let verbs = wantedVerbs(d);
    let verbMatched = !!verbs && verbs.some(v => e.verbs.some(ev => verbMatches(ev, v)));
    if(verbs && !verbMatched && !loose) return null;
    let want = wantedKind(d);
    let parts = [];
    if(!d.prevPrep && d.prevType == 'location' && locationPart(e)) parts.push(locationPart(e));
    for(let part of d.prevPrep ? [d.prevPrep] : ['obj', 'to', 'from']){
        if(e.parts[part] == want) parts.push(part);
    }
    // "warp a [[ship]] and send an [[away team]] to the [[location]] you warped to": the
    // destination is often not spelled out in the text, but is recorded when the action runs.
    if(d.prevPrep && verbMatched && e.parts[d.prevPrep] == 'none') parts.push(d.prevPrep);
    let fits = parts.map(accept);
    let i = fits.indexOf('known');
    if(i == -1) i = fits.findIndex(f => f);
    return i == -1 ? null : parts[i];
}

// ---------------------------------------------------------------- could it be that card?
// Nearest-producer binding is right most of the time, but a reference can only mean a card the
// sentence lets it mean. Three things rule a candidate out, all generic rather than per card:
//  - its suit: "that [[person]]" is not the [[incident]] you took (Quantum Fissure). Traits and
//    card names are looked up in the card data, so a [[lower decker]] is known to be a person;
//  - what the verb needs: only locations have Control operations ("discard a [[lower decker]]
//    to trigger that card's control operation" means the location, Jack Ransom);
//  - where the earlier action left it: you can only take a card that is still in the Market or
//    Encounters, and only recall/refresh a card in play (U.S.S. Beagle's "log a controlled
//    [[location]] to take it" takes the looked-at encounter; Kes's "discard a card to: recall
//    them" recalls Neelix or the doctor).
// Anything not known stays possible, and if nothing passes the old nearest-producer choice
// stands, so these rules only ever move a binding off a card it cannot be.

const SUITS = ['person', 'mission', 'directive', 'cargo', 'ship', 'location', 'ally', 'incident', 'encounter', 'captain', 'status'];

let suitIndex = null, suitIndexFor = null;
function cardSuitIndex(){
    let r = get(res) || {};
    let cards = r.allCards || r.cards;
    if(suitIndex && cards === suitIndexFor) return suitIndex;
    suitIndexFor = cards;
    suitIndex = {traits: new Map(), names: new Map()};
    let add = (map, key, suits) => {
        key = String(key).toLowerCase();
        if(!map.has(key)) map.set(key, new Set());
        for(let s of suits) map.get(key).add(s);
    };
    for(let c of possArray(cards)){
        if(!c || !Array.isArray(c.suits)) continue;
        if(c.name) add(suitIndex.names, c.name, c.suits);
        for(let t of possArray(c.traits)) add(suitIndex.traits, t, c.suits);
    }
    return suitIndex;
}

// The suits a type phrase can be ("controlled location", "lower decker"), or null if unknown.
function suitsOfType(t){
    if(!t) return null;
    if(Array.isArray(t)){
        let all = new Set();
        for(let x of t.flat(2)){
            let s = suitsOfType(x);
            if(!s) return null;
            s.forEach(v => all.add(v));
        }
        return all.size ? all : null;
    }
    if(typeof t == 'object') return suitsOfType(t.specific || t.types || t.either_or || null);
    if(typeof t != 'string') return null;
    let s = t.toLowerCase().replace(/\[\[|\]\]/g, '').trim();
    if(s.includes('non-') || s.includes(' or ')) return null;
    let words = s.split(/[\s/]+/);
    let suits = SUITS.filter(x => words.includes(x));
    if(suits.length) return new Set(suits);
    return cardSuitIndex().traits.get(s) || null;
}

// The suits of the card whose text is being bound, when the caller knows it (bindPrevObjs).
let selfSuits = null;

function suitsOfObj(obj){
    if(isSelfObj(obj)) return selfSuits; // "this card": known only from the card being bound
    if(!obj || typeof obj != 'object' || Array.isArray(obj)) return null;
    let c = obj.card;
    if(c && typeof c == 'object' && c.type == 'card' && typeof c.card == 'string') return cardSuitIndex().names.get(c.card.toLowerCase()) || null;
    if(c && typeof c == 'object' && !isBackreference(c)) return suitsOfType(c.types || null);
    return suitsOfType(obj.types || null);
}

// Where an action leaves the card it acted on. Verbs not listed leave it where it was.
const ZONE_AFTER = {
    log: 'log', log_assimilate: 'log', assimilate: 'log', discard: 'discard', dismiss: 'discard', junk: 'junk',
    spend: 'spent', take: 'owned', gain: 'owned', draw: 'owned', recall: 'hand',
    play: 'play', freeplay: 'play', deploy: 'play', enlist: 'play', beam: 'play', warp: 'play', duty: 'play'
};

// The set of places the card can be after any of these verbs, or null if one of them is unknown.
function zoneAfter(verbs){
    let zones = new Set();
    for(let v of verbs){
        let key = Object.keys(ZONE_AFTER).find(k => verbMatches(v, k));
        if(!key) return null;
        zones.add(ZONE_AFTER[key]);
    }
    return zones.size ? zones : null;
}

// Where the card must be for a verb to act on it, as the runtime checks (filterBasedOnAction).
const ZONE_NEEDED = {take: [], recall: ['play', 'owned'], refresh: ['play', 'owned'], dismiss: ['play', 'owned'],
    exhaust: ['play', 'owned'], warp: ['play', 'owned'], gain_control: ['play', 'owned']};
// Suits a verb can act on at all; 'verb.to' is where it sends something.
const SUITS_NEEDED = {control: ['location'], gain_control: ['location'], warp: ['ship'],
    'away.to': ['location'], 'warp.to': ['location'], 'teleport.to': ['location']};

function neededFor(verb, map){
    let key = verb && Object.keys(map).find(k => verbMatches(verb, k));
    return key ? map[key] : null;
}

// false: it cannot be; 'known': its suit is known to be what is wanted; true: not ruled out.
// `verb` is the verb acting on the reference ('away.to' when it is where an away team goes).
function couldBe(e, part, d, verb){
    if(e.skipped) return false; // produced by a branch that did not run (see otherwise)
    let suits = e.suits && e.suits[part];
    let wanted = [suitsOfType(d.prevType && !['card', 'cards'].includes(d.prevType) ? d.prevType : null),
        suitsOfType(d.filter && d.filter.types), neededFor(verb, SUITS_NEEDED)].filter(w => w);
    if(suits){
        for(let w of wanted){
            if(![...w].some(s => suits.has(s))) return false;
        }
    }
    // The zone is that of the object; a trigger is mid-action ("When taking an [[incident]],
    // ... take it from the Junk"), so it has not arrived anywhere yet.
    let zones = part == 'obj' && !isPseudoSlot(e.slot) ? e.zone : null;
    let needed = verb && !verb.includes('.') ? neededFor(verb, ZONE_NEEDED) : null;
    if(zones && needed && ![...zones].some(z => needed.includes(z))) return false;
    return suits && wanted.length ? 'known' : true;
}

function bindRef(d, ctx){
    if(d.bind) return;
    // 'the same [[trait]]' is value matching, not a backreference to an earlier action.
    if(d.same){
        d.bind = {kind: 'same'};
        return;
    }
    // A singular "it"/"the card" wants a single producer; only if none exists does it fall back
    // to a many-producer (which then trips the runtime "it resolved to N cards" guard).
    let wantOne = !!d.it && (d.num === 1 || d.num == null);
    let hit = null, part = null, loose = false;
    // passes: [loose verb?, allow a many-producer?] – exact+single first, loosen stepwise.
    let passes = wantOne ? [[false, false], [true, false], [false, true], [true, true]]
                         : [[false, true], [true, true]];
    // "discard a card to refresh that [[ship]]": a typed reference passes over untyped producers
    // first, so it finds the trigger's ship rather than the discarded card.
    let typed = !!d.prevType && !['card', 'cards'].includes(d.prevType);
    // First only among cards the reference could be (couldBe); failing that, as before.
    // The verb only constrains its own object: in "dismiss a card sharing a trait with the
    // logged card" the logged card is not what gets dismissed.
    let verb = ctx.object === d ? ctx.verb : ctx.toObject === d && ctx.verb ? ctx.verb + '.to' : null;
    let checks = [e => p => couldBe(e, p, d, verb), () => () => true];
    let accept = null;
    for(let check of checks){
        for(let skipGeneric of typed ? [true, false] : [false]){
            for(let [loosePass, allowMany] of passes){
                for(let i = ctx.visible.length - 1; i >= 0; i--){
                    let e = ctx.visible[i];
                    if(!allowMany && e.single === false) continue;
                    if(skipGeneric && e.generic) continue;
                    part = matchPart(e, d, loosePass, check(e));
                    if(part){
                        hit = e;
                        loose = loosePass;
                        accept = check;
                        break;
                    }
                }
                if(hit) break;
            }
            if(hit) break;
        }
        if(hit) break;
    }
    if(!hit){
        d.bind = {kind: 'unbound'};
        return;
    }
    if(part == 'obj' && hit.via == 'condition' && hit.singular && d.num == null && !d.it && !d.prevType && !d.prevVerb){ d.it = true; d.num = 1; }
    if(part == 'obj' && hit.alias && hit.conditional){
        d.bind = {...hit.alias};
        return;
    }
    let bind = isPseudoSlot(hit.slot) ? {kind: hit.slot} : {kind: 'slot', slot: hit.slot, part};
    if(isPseudoSlot(hit.slot) && part != 'obj') bind.part = part;
    if(hit.choice !== undefined){
        // The hit sits in one branch of a choice: any sibling branch could have produced it
        // instead, so bind to the alternatives and let the runtime take whichever ran.
        let perBranch = new Map();
        for(let e of ctx.visible){
            if(e.choice !== hit.choice) continue;
            if(matchPart(e, d, loose, accept(e)) != part) continue;
            perBranch.set(e.branch, e.slot); // last match in each branch wins
        }
        if(perBranch.size > 1) bind = {kind: 'slots', slots: [...perBranch.values()], part};
    }
    if(hit.orGroup && bind.kind == 'slot'){
        let slots = ctx.visible.filter(e => e.orGroup == hit.orGroup && matchPart(e, d, loose, accept(e)) == part).map(e => e.slot);
        if(slots.length > 1) bind = {kind: 'slots', slots, part};
    }
    if(loose) bind.loose = true; // the named verb was not found: worth watching in shadow mode
    if(hit.via) bind.via = hit.via; // resolvePrevObj falls back to the scan for these
    d.bind = bind;
}

// The object(s) an "if ..." tests, which its body can refer back to. A `multiple` condition
// ("A and B and C") contributes one per sub-condition. prev_obj subjects are skipped: those
// are themselves backreferences (bound elsewhere), not new referents.
function conditionSubjects(node){
    let out = [];
    let add = n => {
        let s = n.obj || (n.types ? {types: n.types} : null);
        // `owner` is the node the runtime resolves, so its slot can be stamped where
        // resolveCondition will find it – on the sub-condition for a compound test.
        if(s && !isBackreference(s.card || s)) out.push({owner: n, obj: s});
    };
    add(node);
    for(let sc of possArray(node.conditions)) add(sc);
    return out;
}

// The subjects of a test that are themselves references to something earlier ("If the
// discarded card is a [[ship]]"): conditionSubjects leaves these out.
function backrefSubjects(node){
    let out = [];
    for(let n of [node, ...possArray(node.conditions)]){
        let s = n && n.obj;
        let ref = s && (isBackreference(s) ? s : isBackreference(s.card) ? s.card : null);
        if(ref) out.push({owner: n, obj: ref});
    }
    return out;
}

// A trigger ("After gaining an [[ally]], ...", "When this card enters your hand, ...") names
// the object its body refers back to.
function triggerEntry(condition){
    let c = Array.isArray(condition) ? condition[0] : condition;
    if(!c || typeof c != 'object') return null;
    if(isSelfObj(c.obj)) return SELF;
    if(typeof c.verb == 'string') return entryFor(c, 'trigger');
    if(c.obj || c.types) return selectEntry(c.obj || {types: c.types}, 'trigger');
    // A verbless, objectless trigger ("When this card is revealed in the Market / leaves play")
    // is a statement about this card, so a following "it" is this card.
    return SELF;
}

// Walk a subtree with an extra referent visible only inside it, then publish whatever the
// subtree produced to the parent scope (but not the scoped referent itself).
function scoped(children, ctx, entry){
    let base = ctx.visible.length;
    let extra = (Array.isArray(entry) ? entry : entry ? [entry] : []).filter(Boolean);
    let sub = {...ctx, visible: [...ctx.visible, ...extra]};
    for(let c of children) walk(c, sub);
    ctx.choices = sub.choices;
    ctx.visible.push(...sub.visible.slice(base + extra.length));
}

// Everything under a node except the keys already consumed by the caller.
function children(node, except = []){
    return Object.keys(node).filter(k => k != 'bind' && !except.includes(k)).map(k => node[k]);
}

// Nearest earlier referent for a marker form: a card producer if there is one ("its location"
// means a card's location), otherwise any producer ("for every 6 [[drone]] regenerated this
// way" counts a token producer). Scoped referents count too – "for each [[ship]] ... send an
// [[away team]] to its [[location]]" means the loop variable.
function nearestProducer(ctx){
    for(let kind of ['card', null]){
        for(let i = ctx.visible.length - 1; i >= 0; i--){
            let e = ctx.visible[i];
            if(kind && e.parts.obj != kind) continue;
            return e;
        }
    }
    return null;
}

// Backreferences the grammar encodes as marker strings on a field rather than as prev_obj nodes.
// At runtime these route through resolveLocCondition -> resolvePrevObj (see parse.js), which
// still reads the history scan, so we bind them with `via` (resolveBinding falls back). Binding
// them here means the engine *recognises* them: they no longer read as fresh producers, and the
// report can see them. Keyed by the field the marker sits on.
const PREV_MARKERS = {
    location: ['prev_obj', 'prev_beamed', 'prev_beamed_ship', 'prev_loc', 'same_place', 'same'],
    types: ['prev_type'],   // a previously chosen trait / token type
    place: ['prev_deck'],   // a previously chosen deck
    num: ['same'],          // "send the same number of [[away team]]"
    format: ['prev_num']    // on a `per` node
};

function recogniseMarkers(node, ctx){
    let mark = (key, via) => {
        let e = nearestProducer(ctx);
        if(!e){ node[key] = {kind: 'unbound', via}; return; }
        if(isPseudoSlot(e.slot)){ node[key] = {kind: e.slot, via}; return; } // self / loop / trigger
        // "there" after "recall a [[ship]] from the same [[location]]" is that location
        let part = key == 'locationBind' && locationPart(e) || 'obj';
        node[key] = {kind: 'slot', slot: e.slot, part, via};
    };
    for(let field of Object.keys(PREV_MARKERS)){
        if(typeof node[field] == 'string' && PREV_MARKERS[field].includes(node[field])) mark(field + 'Bind', node[field]);
    }
    // "its location" / a card used to fulfil a goal: prev_obj-shaped nodes with their own type.
    if(node.type == 'prev_loc' || node.type == 'prev_goal') mark('bind', node.type);
}

// "When X would be dismissed, recall it instead": in a replacement the "it" is always the object
// the reaction fired on, never whatever else happens to be in the history (e.g. the mission whose
// logging caused the dismissal). A named card is substituted outright; otherwise the reference is
// forced to the trigger (live even with bindings off), narrowed to what the condition matches.
// Bare "them" is singular only for one [[person]] ("After you log a [[person]], beam them here
// instead"); anything else it is a plural.
function singularThem(ref, cond){
    let types = cond && cond.types;
    return ref.num == null && !ref.prevVerb && !ref.prevType && cond.num == 1
        && (Array.isArray(types) ? types.length == 1 && types[0] == 'person' : types == 'person');
}

function bindReplacementIt(reaction){
    // Only when nothing earlier in the body produced a card: in "draw a card and discard it
    // instead" the "it" is the drawn card. Costs don't count ("take an [[incident]] to recall it").
    let cond = reaction.condition.obj;
    let produced = false;
    let visit = n => {
        if(!n || typeof n != 'object') return;
        if(Array.isArray(n)) return n.forEach(visit);
        let ref = n.obj && n.obj.card;
        if(n.replace && ref && ref.type == 'prev_obj' && (ref.it || singularThem(ref, cond)) && !produced){
            if(cond && cond.card && cond.card.type == 'card' && cond.card.card) n.obj.card = {...cond.card};
            else ref.bind = {kind: 'trigger', forced: true, types: cond && (cond.types || (cond.card && cond.card.types)) || null};
        }else if(n.verb && n.obj && !(ref && ref.type == 'prev_obj')){
            produced = true;
        }
        Object.keys(n).forEach(k => k != 'condition' && k != 'cost' && visit(n[k]));
    };
    visit(reaction.action);
}

function walk(node, ctx){
    if(!node || typeof node != 'object') return;
    if(Array.isArray(node)){
        for(let n of node) walk(n, ctx);
        return;
    }

    recogniseMarkers(node, ctx);

    if(isBackreference(node)){
        bindRef(node, ctx);
        for(let c of children(node)) walk(c, ctx); // a descriptor can nest further references
        return;
    }

    if(node.type == 'choice' && Array.isArray(node.choices)){
        let choice = ctx.choices++;
        let base = ctx.visible.length;
        let produced = [];
        let branch = 0;
        for(let b of node.choices){
            // Branches are mutually exclusive, so none of them sees the others.
            let sub = {...ctx, visible: ctx.visible.slice(0, base)};
            walk(b, sub);
            for(let e of sub.visible.slice(base)) produced.push({...e, choice, branch});
            ctx.choices = sub.choices;
            branch++;
        }
        for(let c of children(node, ['choices'])) walk(c, ctx);
        ctx.visible.push(...produced);
        return;
    }

    // A reaction carries its trigger in `condition`; the trigger is a referent for its body.
    // The triggering action itself runs elsewhere, so it takes no slot of ours.
    if(node.type == 'reaction' && node.condition){
        let trigger = triggerEntry(node.condition);
        walk(node.condition, {...ctx, refsOnly: true});
        scoped(children(node, ['condition']), ctx, trigger);
        bindReplacementIt(node);
        return;
    }

    // "If a [[person]] is in the Bot Discard pile, promote it": the tested object is a
    // referent inside the body. Its own backreferences bind to earlier producers first.
    if(node.type == 'condition'){
        // "If the top card of the Bot deck is a [[helmet]], discard it ...; otherwise, log it": an
        // otherwise runs only when the body before it did not, so what that body produced is
        // only a last resort (couldBe), and the card that condition tested is a referent too.
        let prev = node.otherwise ? ctx.lastCondition : null;
        let outer = ctx;
        if(prev) ctx = {...ctx, visible: [...ctx.visible.slice(0, prev.from),
            ...ctx.visible.slice(prev.from, prev.to).map(e => ({...e, skipped: true})),
            ...ctx.visible.slice(prev.to), ...prev.entries]};
        let viewBase = ctx.visible.length;
        let body = node.action;
        let rest = children(node, ['action']);
        if(node.condition == 'previous_verb' && node.verb){
            // "If you gained 3 or more": counting an earlier action, not naming its objects.
            for(let d of collectRefs(rest)){
                if(d.bind) continue;
                let hit = null;
                for(let i = ctx.visible.length - 1; i >= 0; i--){
                    if(ctx.visible[i].verbs.some(v => verbMatches(v, node.verb))){ hit = ctx.visible[i]; break; }
                }
                d.bind = hit ? {kind: 'count', slot: hit.slot} : {kind: 'unbound'};
            }
        }else{
            for(let c of rest) walk(c, {...ctx, refsOnly: true}); // a test names objects, it does not act
        }
        // The subject sits in `obj`, or bare in `types` ("3+ [[person]] beamed here"), or –
        // for a compound "A and B and C" condition – one per sub-condition ("... the Bot has a
        // secured neutral [[location]] ... assimilate that [[location]]"). Each becomes a
        // referent for the body; kind-matching picks the right one ("that location" vs a track).
        // "If you have *Neelix* or a [[doctor]] on duty, ... recall them": either subject may be the
        // one that is there, so a reference to one of them means whichever it was
        let either = node.num == 'any' || possArray(node.conditions).some(c => c && c.separator == 'or');
        let orGroup = either ? {} : null; // an identity, not a slot: slots must not be renumbered
        let entries = conditionSubjects(node).map(({owner, obj}) => {
            if(isSelfObj(obj)) return SELF;
            let slot = nextSlot(ctx);
            owner.subjectSlot = slot; // resolveCondition records what the test resolved to here
            let e = selectEntry(obj, slot, node.verb ? [node.verb] : [], 'condition');
            if(testsMany(owner)) e.single = false;
            else if(testsOne(owner)) e.singular = true;
            if(orGroup) e.orGroup = orGroup;
            // A type or location test is about a card that is there either way; after "if you
            // have a [[doctor]]" fails, there is no doctor for an otherwise to mean.
            if(['type_match', 'loc_test'].includes(owner.condition)) e.either = true;
            // "If the *U.S.S. Enterprise* is at a controlled [[location]], ... that card's control
            // operation": the location the subject was found at is a referent too
            // ("If you have an [[away team]] on a neutral [[location]] ..., take control of it")
            let on = obj.location && typeof obj.location == 'object' && (obj.location.loc_on || obj.location.loc_at);
            if(owner.condition == 'loc_test' || on){
                e.parts.to = 'card';
                e.suits.to = suitsOfType(on) || new Set(['location']);
            }
            return e;
        });
        // "If the [[weapon]] is [[kazon]], dismiss it": a test of an earlier card makes that card
        // the nearest referent again (rather than whatever the text did in between)
        for(let {obj} of backrefSubjects(node)){
            let b = obj.bind;
            let key = b && (b.kind == 'slot' && (!b.part || b.part == 'obj') ? b.slot : ['trigger', 'self', 'loop'].includes(b.kind) ? b.kind : null);
            let earlier = key && [...ctx.visible].reverse().find(e => e.slot == key);
            if(earlier) entries.push({...earlier, conditional: false, either: true, resurfaced: true});
        }
        // After the test, "the [[weapon]]" can still mean the weapon "If you have a [[weapon]] ..."
        // tested ("If the [[weapon]] is [[kazon]], dismiss it"), so its subjects stay visible.
        let subjects = entries.filter(e => !isPseudoSlot(e.slot) && !e.resurfaced);
        let base = ctx.visible.length;
        scoped(possArray(body), ctx, entries);
        ctx.visible.splice(base, 0, ...subjects);
        // Seen from after the condition, the body's actions may never have run.
        for(let i = base; i < ctx.visible.length; i++) ctx.visible[i] = {...ctx.visible[i], conditional: true};
        if(ctx !== outer){
            outer.visible.push(...ctx.visible.slice(viewBase));
            outer.choices = ctx.choices;
        }
        let produced = outer.visible.length;
        outer.lastCondition = {from: produced - (ctx.visible.length - viewBase), to: produced, entries: [...entries.filter(e => e.either), ...(prev ? prev.entries : [])]};
        return;
    }

    // "For each X ...: do something to it" – the loop variable is the current X.
    if(node.type == 'action_per' && node.per && node.per.obj){
        for(let c of children(node, ['action'])) walk(c, ctx);
        scoped(possArray(node.action), ctx, isSelfObj(node.per.obj) ? SELF : selectEntry(node.per.obj, 'loop', [], 'loop'));
        return;
    }
    // "Draw 3 cards. For each: ... discard it" – the runtime loops over the previous action's
    // results one at a time, so "it" is the current one, not the whole earlier result.
    if(node.type == 'action_per' && node.per && node.per.format == 'selected'){
        for(let c of children(node, ['action'])) walk(c, ctx);
        let last = [...ctx.visible].reverse().find(e => !isPseudoSlot(e.slot));
        let entry = last ? {slot: 'loop', verbs: last.verbs, parts: {obj: last.parts.obj, to: 'none', from: 'none'}, via: 'loop'} : null;
        scoped(possArray(node.action), ctx, entry);
        return;
    }

    // A goal lists what you must have, and a later element can refer back to the earlier ones
    // ("... and a [[scientist]] in play, one of them on duty").
    if(Array.isArray(node.elements)){
        let sub = {...ctx, visible: [...ctx.visible]};
        for(let el of node.elements){
            walk(el, sub);
            if(el && el.obj){
                el.slot = nextSlot(ctx);
                sub.visible.push(selectEntry(el.obj, el.slot, [], 'goal'));
            }
        }
        ctx.choices = sub.choices;
        for(let c of children(node, ['elements'])) walk(c, ctx);
        return;
    }

    // "When this card is enlisted, put it into play" – the enlisted card is this card.
    if(node.on_enlist){
        scoped(possArray(node.on_enlist), ctx, SELF);
        for(let c of children(node, ['on_enlist'])) walk(c, ctx);
        return;
    }

    if(isAction(node)){
        let outerVerb = ctx.verb, outerObject = ctx.object, outerTo = ctx.toObject;
        // the reference this action acts on directly ("take it", "trigger that card's ..."), or
        // sends something to ("send another [[away team]] to it"), if any
        let refIn = o => o && typeof o == 'object' ? (isBackreference(o) ? o : isBackreference(o.card) ? o.card : null) : null;
        ctx.verb = node.verb;
        ctx.object = refIn(node.obj);
        ctx.toObject = refIn(node.to);
        for(let c of children(node)) walk(c, ctx); // an action cannot refer to itself
        ctx.verb = outerVerb;
        ctx.object = outerObject;
        ctx.toObject = outerTo;
        ctx.lastCondition = null; // an otherwise follows its condition directly
        if(ctx.refsOnly) return;
        let slot = nextSlot(ctx);
        node.slot = slot;
        let e = entryFor(node, slot);
        ctx.visible.push(e);
        // seqVerbs run straight after this action on the same object (see parsedAction), so
        // they share its slot and only add verb names a later reference can ask for.
        for(let v of possArray(node.seqVerbs)){
            if(v && v.verb) ctx.visible.push({...e, verbs: [v.verb]});
        }
        return;
    }

    for(let c of children(node)) walk(c, ctx);
}

// Slot ids are `<unit>:<n>` – scoped to the compilation unit they were stamped in, so two
// trees in flight at once (a card duplicating another card's operations, a reaction firing
// mid-effect) can share one slot table without overwriting each other. The runtime also uses
// the prefix to tell "this action whiffed, the answer is nothing" from "this unit is not
// running, I have no idea": see frames.js.
function nextSlot(ctx){
    return `${ctx.unit}:${ctx.counter.n++}`;
}

// cyrb53: a fast 53-bit string hash. Collisions are what matter here (two different trees on
// one unit could read each other's slots); at a few thousand units that is ~1e-9.
function hash53(str){
    let h1 = 0xdeadbeef, h2 = 0x41c6ce57;
    for(let i = 0; i < str.length; i++){
        let ch = str.charCodeAt(i);
        h1 = Math.imul(h1 ^ ch, 2654435761);
        h2 = Math.imul(h2 ^ ch, 1597334677);
    }
    h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
    h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
    return 4294967296 * (2097151 & h2) + (h1 >>> 0);
}

// A unit is named after the tree it stamps, not the order trees happen to be bound in. Frames
// carry these names out of the session – in saves, in state synced to the other player, in
// effect offers – so the same card must get the same unit on every load and every client,
// and two different cards must never meet on one.
let fallbackUnits = 0;
function unitFor(clauses){
    try{
        return 'u' + hash53(JSON.stringify(clauses)).toString(36);
    }catch(e){ // not serialisable (a cycle): still unique, just not stable across sessions
        return `x${++fallbackUnits}`;
    }
}

// Re-binding a tree must not renumber it: parsedCardText is rebound on every load and on the
// unchanged-card path, and effects/response share these nodes. The unit already stamped into
// any slot in the tree is therefore reused, which keeps bindPrevObjs idempotent.
function existingUnit(node, seen = new Set()){
    if(!node || typeof node != 'object' || seen.has(node)) return null;
    seen.add(node);
    if(typeof node.slot == 'string' && node.slot.includes(':')) return node.slot.split(':')[0];
    for(let k of Object.keys(node)){
        let found = existingUnit(node[k], seen);
        if(found) return found;
    }
    return null;
}

// One clause ("Play: ...", "Reaction: ...") is one compilation unit: the runtime resets its
// cache per activation, so references never cross clauses.
// `card` (optional) is the card the text belongs to: knowing its suits lets "that [[ship]]"
// pass over "deploy this card" when this card is not a ship.
export function bindPrevObjs(parsed, card = null){
    if(!parsed) return parsed;
    let clauses = Array.isArray(parsed) ? parsed : possArray(parsed.response || parsed.effects);
    if(!Array.isArray(clauses)) return parsed;
    let counter = {n: 0};
    let unit = existingUnit(clauses) || unitFor(clauses);
    let outerSelf = selfSuits;
    selfSuits = card && Array.isArray(card.suits) && card.suits.length ? new Set(card.suits) : null;
    try{
        for(let clause of clauses){
            if(clause && typeof clause == 'object') walk(clause, {visible: [], counter, choices: 0, unit});
        }
    }finally{
        selfSuits = outerSelf;
    }
    return parsed;
}

// For the test/report: every prev_obj in a tree, with its binding.
export function collectRefs(node, out = []){
    if(!node || typeof node != 'object') return out;
    if(Array.isArray(node)){
        for(let n of node) collectRefs(n, out);
        return out;
    }
    if(isBackreference(node)) out.push(node);
    for(let k of Object.keys(node)) collectRefs(node[k], out);
    return out;
}

export const _internals = {kindOfObj, typeKind, wantedKind, wantedVerbs};
